import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { http, notify, errorMessage } from "./api";
import Login from "./components/Login";
import Register from "./components/Register";
import Shell from "./components/Shell";
import CustomerUpload from "./components/CustomerUpload";
import AttendanceKiosk from "./components/AttendanceKiosk";
import { Website, Landing, LegalPage } from "./components/site/MarketingSite";
import "@/App.css";

const customerMatch = window.location.pathname.match(/^\/customer\/upload\/([\w-]+)/);
const registerMatch = window.location.pathname === "/register";
const kioskMatch = ["/kiosk", "/rupeexpert-kiosk", "/rupeexpert-attendance", "/rupeexpert"]
  .includes(window.location.pathname);
const websiteMatch = window.location.pathname.startsWith("/website");
const landingMatch = window.location.pathname.startsWith("/apply");
const legalSlug = ["/privacy", "/terms", "/disclosures"].includes(window.location.pathname)
  ? window.location.pathname.slice(1) : null;

function ForceChangePassword({ user, onDone }) {
  const [current, setCurrent] = useState("");
  const [nxt, setNxt] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nxt.length < 6) return notify.error("New password must be at least 6 characters");
    if (nxt !== confirm) return notify.error("Passwords do not match");
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: current, new_password: nxt });
      notify.success("Password updated");
      onDone();
    } catch (err) { notify.error(err, errorMessage(err) || "Could not change password"); }
    finally { setBusy(false); }
  };

  return (
    <main className="login-page" data-testid="force-change-password-page">
      <div className="login-aside">
        <div className="brand-mark">RX</div>
        <p className="eyebrow">FIRST LOGIN</p>
        <h1>Set your<br /><em>personal password.</em></h1>
        <p className="login-copy">
          Your administrator issued a temporary password. Please choose one only you know before continuing.
        </p>
      </div>
      <form className="login-form" onSubmit={submit}>
        <div>
          <p className="eyebrow">SECURE ACCESS</p>
          <h2>Change password</h2>
          <p className="muted">Signed in as {user.name} ({user.employee_code || user.email})</p>
        </div>
        <label>Current (temporary) password
          <input data-testid="fcp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label>New password (min 6 characters)
          <input data-testid="fcp-new" type="password" required value={nxt} onChange={(e) => setNxt(e.target.value)} />
        </label>
        <label>Confirm new password
          <input data-testid="fcp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        <button data-testid="fcp-submit" className="primary-button" disabled={busy}>
          {busy ? "Saving…" : "Set new password"}
        </button>
      </form>
    </main>
  );
}

function AuthenticatedApp() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    http.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const mustChange = user && user.must_change_password === true;

  return (
    <>
      <Toaster position="top-right" richColors closeButton />
      {checking ? (
        <div className="loading">Loading Rupee Xpert…</div>
      ) : !user ? (
        <Login onLogin={setUser} />
      ) : mustChange ? (
        <ForceChangePassword
          user={user}
          onDone={() => http.get("/auth/me").then((r) => setUser(r.data))}
        />
      ) : (
        <Shell user={user} onLogout={() => setUser(null)} />
      )}
    </>
  );
}

function App() {
  if (customerMatch) {
    return <CustomerUpload token={customerMatch[1]} />;
  }
  if (registerMatch) {
    return (
      <>
        <Toaster position="top-right" richColors closeButton />
        <Register />
      </>
    );
  }
  if (kioskMatch) {
    return (
      <>
        <Toaster position="top-right" richColors closeButton />
        <AttendanceKiosk />
      </>
    );
  }
  if (websiteMatch) {
    return (
      <>
        <Toaster position="top-right" richColors closeButton />
        <Website />
      </>
    );
  }
  if (landingMatch) {
    return (
      <>
        <Toaster position="top-right" richColors closeButton />
        <Landing />
      </>
    );
  }
  if (legalSlug) {
    return (
      <>
        <Toaster position="top-right" richColors closeButton />
        <LegalPage slug={legalSlug} />
      </>
    );
  }
  return <AuthenticatedApp />;
}

export default App;
